import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { UserCheck, Check, ArrowLeft, LogOut, AlertTriangle, Search, ShieldCheck, Delete } from "lucide-react";
import api from "@/lib/api";

// Kiosk check-in flow (no photos):
//   start → keypad → pick (name list) → welcome
//   sign-out: start → signout-keypad → (signout-multi if needed) → signout-done
//   admin help: start → help-passcode → help-search → welcome

function Digit({ v }) {
  return (
    <div className={`w-14 h-16 sm:w-16 sm:h-20 rounded-2xl flex items-center justify-center text-3xl sm:text-4xl font-mono font-bold ${v ? "bg-white text-slate-900 shadow" : "bg-white/30 text-slate-400"}`}>
      {v || "○"}
    </div>
  );
}

function Keypad({ onDigit, onBackspace, onClear }) {
  const rows = [["1","2","3"],["4","5","6"],["7","8","9"],["clear","0","back"]];
  return (
    <div className="grid grid-cols-3 gap-3 w-full max-w-sm mx-auto" data-testid="staff-keypad">
      {rows.flat().map((k) => {
        if (k === "back") return (
          <button key={k} onClick={onBackspace} className="bg-white/80 hover:bg-white rounded-2xl text-2xl font-bold text-slate-700 py-5 flex items-center justify-center shadow" data-testid="keypad-back">
            <Delete className="w-6 h-6" />
          </button>
        );
        if (k === "clear") return (
          <button key={k} onClick={onClear} className="bg-white/80 hover:bg-white rounded-2xl text-sm font-bold text-slate-700 py-5 shadow" data-testid="keypad-clear">
            CLEAR
          </button>
        );
        return (
          <button key={k} onClick={() => onDigit(k)} className="bg-white hover:bg-blue-50 active:bg-blue-100 rounded-2xl text-3xl font-bold text-slate-900 py-5 shadow" data-testid={`keypad-${k}`}>
            {k}
          </button>
        );
      })}
    </div>
  );
}

export default function StaffCheckIn() {
  const navigate = useNavigate();
  const [step, setStep] = useState("start");
  const [last4, setLast4] = useState("");
  const [matches, setMatches] = useState([]);            // list of staff cards from lookup
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [welcome, setWelcome] = useState(null);

  // sign-out state
  const [signoutMatches, setSignoutMatches] = useState(null);
  const [signoutDone, setSignoutDone] = useState(null);

  // admin help
  const [helpPass, setHelpPass] = useState("");
  const [helpSearch, setHelpSearch] = useState("");
  const [helpResults, setHelpResults] = useState([]);
  const [helpToken, setHelpToken] = useState(null);

  // Welcome auto-dismiss (4s)
  useEffect(() => {
    if (step !== "welcome") return undefined;
    const t = setTimeout(() => navigate("/"), 4000);
    return () => clearTimeout(t);
  }, [step, navigate]);
  useEffect(() => {
    if (step !== "signout-done") return undefined;
    const t = setTimeout(() => navigate("/"), 4000);
    return () => clearTimeout(t);
  }, [step, navigate]);

  const reset = () => {
    setLast4(""); setMatches([]); setError(""); setWelcome(null);
    setSignoutMatches(null); setSignoutDone(null);
    setHelpPass(""); setHelpSearch(""); setHelpResults([]);
  };

  // ---- keypad handlers ----
  // Auto-submit at 4 digits happens inside onDigit (avoids set-state-in-effect).
  const onDigit = (d) => {
    setLast4((prev) => {
      if (prev.length >= 4) return prev;
      const next = prev + d;
      if (next.length === 4) {
        const action = step === "signout-keypad" ? submitSignout : lookup;
        Promise.resolve().then(() => action(next));
      }
      return next;
    });
  };
  const onBack = () => setLast4((x) => x.slice(0, -1));
  const onClear = () => setLast4("");

  const lookup = useCallback(async (l4) => {
    setSubmitting(true);
    setError("");
    let lastErr = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const r = await api.post("/staff-checkin/lookup", { phone_last_4: l4 }, { timeout: 12000 });
        const d = r.data || {};
        if (d.match_type === "none") {
          setStep("no-match");
        } else if (d.match_type === "single") {
          // Normalize single hit into a 1-item list so the name picker is the same UI in both cases.
          setMatches([{
            crew_member_id: d.crew_member_id,
            name: d.name,
            division_name: d.division_name,
            position: d.position,
          }]);
          setStep("pick");
        } else if (d.match_type === "multiple") {
          setMatches(d.matches || []);
          setStep("pick");
        } else {
          setStep("no-match");
        }
        setSubmitting(false);
        return;
      } catch (e) {
        lastErr = e;
        const status = e?.response?.status;
        if (status && status >= 400 && status < 500 && status !== 429) break;
        await new Promise((res) => setTimeout(res, 600));
      }
    }
    const detail = lastErr?.response?.data?.detail;
    const code = lastErr?.code;
    let msg;
    if (typeof detail === "string") msg = detail;
    else if (code === "ECONNABORTED") msg = "Connection timed out. Check Wi-Fi and try again.";
    else if (lastErr?.message?.includes("Network")) msg = "No internet — check Wi-Fi and try again.";
    else msg = "Lookup failed — please try again or ask the front desk.";
    setError(msg);
    setSubmitting(false);
  }, []);

  // ---- sign-out ----
  const submitSignout = useCallback(async (l4) => {
    setSubmitting(true); setError("");
    try {
      const r = await api.post("/staff-checkin/sign-out", { phone_last_4: l4 }, { timeout: 12000 });
      if (r.data.error === "already_signed_out") {
        const t = r.data.signed_out_at ? new Date(r.data.signed_out_at * 1000).toLocaleTimeString([], {hour:'numeric', minute:'2-digit'}) : null;
        const name = r.data.name ? `, ${r.data.name},` : "";
        setError(`You${name} already checked out${t ? ` at ${t}` : ""} today. If you're back, tap Check In on the previous screen.`);
      } else if (r.data.error === "no_match") {
        setError("No active check-in found for that phone. If you haven't checked in yet today, tap Check In on the previous screen.");
      } else if (r.data.matches) {
        setSignoutMatches(r.data.matches);
        setStep("signout-multi");
      } else if (r.data.ok) {
        setSignoutDone({ name: r.data.name });
        setStep("signout-done");
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d
        : Array.isArray(d) ? (d[0]?.msg || "Invalid input")
        : (e?.message || "Sign-out failed. Try again or ask the front desk.");
      setError(msg);
    } finally { setSubmitting(false); }
  }, []);

  const doSignIn = async (crewMemberId, verifiedVia) => {
    setSubmitting(true);
    try {
      const r = await api.post("/staff-checkin/sign-in", {
        crew_member_id: crewMemberId,
        verified_via: verifiedVia || "last4_name_pick",
      });
      setWelcome(r.data);
      setStep("welcome");
    } catch (e) {
      setError(e?.response?.data?.detail || "Sign-in failed");
    } finally { setSubmitting(false); }
  };

  const pickName = async (m) => {
    if (submitting || !m?.crew_member_id) return;
    await doSignIn(m.crew_member_id, "last4_name_pick");
  };

  const pickSignout = async (signin_id) => {
    setSubmitting(true);
    try {
      const r = await api.post(`/staff-checkin/sign-out/${signin_id}`);
      setSignoutDone({ name: r.data.name });
      setStep("signout-done");
    } catch (e) { setError("Sign-out failed"); }
    finally { setSubmitting(false); }
  };

  // ---- admin help mode ----
  const submitHelpPass = async (e) => {
    e?.preventDefault?.();
    setSubmitting(true); setError("");
    try {
      const r = await api.post("/auth/admin-login", { passcode: helpPass });
      setHelpToken(r.data.token);
      setStep("help-search");
    } catch {
      setError("Wrong admin passcode.");
    } finally { setSubmitting(false); }
  };
  const searchStaff = async (q) => {
    if (!q || q.length < 2) { setHelpResults([]); return; }
    try {
      const r = await api.post("/staff-checkin/lookup-by-name", { name_search: q });
      setHelpResults(r.data.items || []);
    } catch { setHelpResults([]); }
  };
  useEffect(() => {
    if (step !== "help-search") return undefined;
    const t = setTimeout(() => searchStaff(helpSearch), 200);
    return () => clearTimeout(t);
  }, [helpSearch, step]);

  const helpSignInPicked = async (m) => {
    if (submitting || !m?.crew_member_id) return;
    setSubmitting(true);
    try {
      const r = await api.post(
        "/admin/staff-checkin/help-sign-in",
        { crew_member_id: m.crew_member_id },
        { headers: { Authorization: `Bearer ${helpToken}` } },
      );
      setWelcome(r.data);
      setStep("welcome");
    } catch (e) {
      setError(e?.response?.data?.detail || "Sign-in failed");
    } finally { setSubmitting(false); }
  };

  // ==================== UI ====================
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-blue-950 via-slate-900 to-blue-900 text-white" data-testid="staff-checkin-page">
      <div className="bg-black/30 backdrop-blur border-b border-white/10 px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <button onClick={() => { reset(); navigate("/"); }} className="p-2 rounded-lg hover:bg-white/10" data-testid="staff-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <UserCheck className="w-6 h-6 text-teal-300" />
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-teal-200/80 font-bold">Rolling River Day Camp</div>
          <h1 className="text-lg font-display font-bold">Staff Check-In</h1>
        </div>
      </div>

      <div className="flex-1 flex items-start justify-center p-4 sm:p-6">
        <div className="w-full max-w-2xl">

          {/* ---------- START ---------- */}
          {step === "start" && (
            <div className="space-y-4" data-testid="staff-start">
              <button onClick={() => { reset(); setStep("keypad"); }}
                      className="w-full rounded-2xl bg-teal-400 hover:bg-teal-300 text-slate-900 font-bold text-xl px-6 py-6 shadow-xl shadow-teal-500/30 inline-flex items-center justify-center gap-3"
                      data-testid="staff-start-checkin">
                <UserCheck className="w-6 h-6" /> Check In
              </button>
              <button onClick={() => { reset(); setStep("signout-keypad"); }}
                      className="w-full rounded-2xl bg-white/10 hover:bg-white/20 text-white border border-white/20 font-bold text-base px-6 py-4 inline-flex items-center justify-center gap-2"
                      data-testid="staff-start-signout">
                <LogOut className="w-5 h-5" /> Check Out
              </button>
              <button onClick={() => { reset(); setStep("help-passcode"); }}
                      className="block mx-auto mt-6 text-xs text-teal-200 hover:text-teal-100 underline-offset-4 hover:underline inline-flex items-center gap-1"
                      data-testid="staff-admin-help">
                <ShieldCheck className="w-3.5 h-3.5" /> Admin help
              </button>
            </div>
          )}

          {/* ---------- KEYPAD (sign-in & sign-out share) ---------- */}
          {(step === "keypad" || step === "signout-keypad") && (
            <div className="space-y-6" data-testid={step === "keypad" ? "staff-keypad-view" : "staff-signout-keypad-view"}>
              <div className="text-center">
                <h2 className="text-2xl font-bold">{step === "keypad" ? "Enter last 4 of phone" : "Check Out — last 4 of phone"}</h2>
                <p className="text-teal-200/80 text-sm mt-1">{step === "keypad" ? "Then tap your name to check in." : "We'll sign you out automatically."}</p>
              </div>
              <div className="flex gap-3 justify-center">
                {[0,1,2,3].map((i) => <Digit key={i} v={last4[i] || ""} />)}
              </div>
              <Keypad onDigit={onDigit} onBackspace={onBack} onClear={onClear} />
              {error && <div className="text-red-300 text-sm text-center" data-testid="staff-keypad-error">{error}</div>}
              <button onClick={() => { reset(); setStep("start"); }} className="block mx-auto text-xs text-teal-200 hover:text-teal-100">Cancel</button>
            </div>
          )}

          {/* ---------- PICK NAME (single & multiple match) ---------- */}
          {step === "pick" && matches.length > 0 && (
            <div className="space-y-4" data-testid="staff-pick-list">
              <div className="text-center">
                <h2 className="text-2xl font-bold">{matches.length === 1 ? "Tap your name to check in" : "Tap your name"}</h2>
                <p className="text-teal-200/80 text-sm mt-1">{matches.length === 1 ? "Confirm it's you." : `${matches.length} staff share these last 4.`}</p>
              </div>
              <div className="space-y-2.5">
                {matches.map((m) => (
                  <button
                    key={m.crew_member_id}
                    onClick={() => pickName(m)}
                    disabled={submitting}
                    className="w-full text-left rounded-2xl bg-white text-slate-900 px-5 py-5 shadow hover:bg-teal-50 active:bg-teal-100 disabled:opacity-60 border border-white/10 flex items-center justify-between"
                    data-testid={`staff-pick-${m.crew_member_id}`}
                  >
                    <div className="min-w-0">
                      <div className="text-xl font-bold truncate">{m.name}</div>
                      {(m.division_name || m.position) && (
                        <div className="text-sm text-slate-500 truncate">
                          {m.division_name || ""}{m.division_name && m.position ? " · " : ""}{m.position || ""}
                        </div>
                      )}
                    </div>
                    <UserCheck className="w-6 h-6 text-teal-600 shrink-0" />
                  </button>
                ))}
              </div>
              {error && <div className="text-red-300 text-sm text-center" data-testid="staff-pick-error">{error}</div>}
              <button onClick={() => { reset(); setStep("start"); }} className="block mx-auto text-xs text-teal-200 hover:text-teal-100">None of these</button>
            </div>
          )}

          {/* ---------- NO MATCH ---------- */}
          {step === "no-match" && (
            <div className="rounded-3xl bg-white text-slate-900 p-6 shadow-2xl text-center space-y-4" data-testid="staff-no-match">
              <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto" />
              <h2 className="text-xl font-bold">Phone not found</h2>
              <p className="text-slate-600 text-sm">Please ask the front desk for help, or tap <strong>Admin help</strong> on the start screen.</p>
              <button onClick={() => { reset(); setStep("start"); }} className="w-full rounded-xl bg-teal-500 hover:bg-teal-600 text-white font-bold py-3" data-testid="staff-no-match-try-again">Try again</button>
            </div>
          )}

          {/* ---------- WELCOME ---------- */}
          {step === "welcome" && welcome && (
            <div className="rounded-3xl bg-white text-slate-900 p-8 shadow-2xl text-center space-y-4" data-testid="staff-welcome">
              <div className="w-20 h-20 rounded-full bg-teal-100 flex items-center justify-center mx-auto">
                <Check className="w-12 h-12 text-teal-600" />
              </div>
              <h2 className="text-3xl font-bold">Welcome, {welcome.name}!</h2>
              <p className="text-slate-600">Have a great day at camp.</p>
              {welcome.already_signed_in && <p className="text-xs text-amber-700">(Already signed in earlier — no new record created.)</p>}
            </div>
          )}

          {/* ---------- SIGN-OUT MULTI ---------- */}
          {step === "signout-multi" && signoutMatches && (
            <div className="rounded-2xl bg-white text-slate-900 p-5 shadow-2xl space-y-2" data-testid="staff-signout-multi">
              <h2 className="font-bold text-lg">Multiple matches — pick yours</h2>
              {signoutMatches.map((m) => (
                <button key={m.signin_id} onClick={() => pickSignout(m.signin_id)} disabled={submitting}
                        className="w-full text-left rounded-xl border border-slate-200 hover:bg-teal-50 px-4 py-3 flex items-center justify-between"
                        data-testid={`staff-signout-pick-${m.signin_id}`}>
                  <div className="font-semibold">{m.name}</div>
                  <LogOut className="w-4 h-4 text-teal-600" />
                </button>
              ))}
              <button onClick={() => { reset(); setStep("start"); }} className="block mx-auto text-xs text-slate-500">None of these</button>
            </div>
          )}

          {step === "signout-done" && signoutDone && (
            <div className="rounded-3xl bg-white text-slate-900 p-8 shadow-2xl text-center space-y-3" data-testid="staff-signout-done">
              <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mx-auto"><Check className="w-8 h-8 text-teal-600" /></div>
              <h2 className="text-2xl font-bold">Goodbye, {signoutDone.name}!</h2>
              <p className="text-xs text-slate-500">See you tomorrow.</p>
            </div>
          )}

          {/* ---------- ADMIN HELP — passcode ---------- */}
          {step === "help-passcode" && (
            <form onSubmit={submitHelpPass} className="rounded-3xl bg-white text-slate-900 p-6 shadow-2xl space-y-4" data-testid="staff-help-passcode">
              <h2 className="text-xl font-bold">Admin Help</h2>
              <p className="text-sm text-slate-600">Enter the admin passcode to sign someone in by name.</p>
              <input type="password" value={helpPass} onChange={(e) => setHelpPass(e.target.value)} autoFocus
                     placeholder="Admin passcode"
                     className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:ring-2 focus:ring-teal-400"
                     data-testid="staff-help-pass-input" />
              {error && <div className="text-red-700 text-sm">{error}</div>}
              <button type="submit" disabled={submitting || !helpPass}
                      className="w-full rounded-xl bg-teal-500 hover:bg-teal-600 text-white font-bold py-3 disabled:opacity-50"
                      data-testid="staff-help-pass-submit">Continue</button>
              <button type="button" onClick={() => { reset(); setStep("start"); }} className="block mx-auto text-xs text-slate-500">Cancel</button>
            </form>
          )}

          {/* ---------- ADMIN HELP — search ---------- */}
          {step === "help-search" && (
            <div className="rounded-3xl bg-white text-slate-900 p-6 shadow-2xl space-y-4" data-testid="staff-help-search">
              <h2 className="text-xl font-bold">Admin Help — sign in by name</h2>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input value={helpSearch} onChange={(e) => setHelpSearch(e.target.value)} autoFocus placeholder="Type a name…"
                       className="w-full pl-10 pr-3 py-3 rounded-xl border border-slate-200 outline-none focus:ring-2 focus:ring-teal-400"
                       data-testid="staff-help-search-input" />
              </div>
              <div className="space-y-2 max-h-80 overflow-auto">
                {helpResults.map((m) => (
                  <button key={m.crew_member_id}
                          onClick={() => helpSignInPicked(m)}
                          disabled={submitting}
                          className="w-full text-left rounded-xl border border-slate-200 hover:bg-teal-50 disabled:opacity-60 px-3 py-3"
                          data-testid={`staff-help-pick-${m.crew_member_id}`}>
                    <div className="font-semibold">{m.name}</div>
                    {m.division_name && <div className="text-xs text-slate-500">{m.division_name}</div>}
                  </button>
                ))}
                {helpSearch.length >= 2 && helpResults.length === 0 && (
                  <div className="text-sm text-slate-500 text-center py-6">No matches.</div>
                )}
              </div>
              {error && <div className="text-red-700 text-sm">{error}</div>}
              <button onClick={() => { reset(); setStep("start"); }} className="block mx-auto text-xs text-slate-500">Cancel</button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
